/**
 * DomSandbox — DOM resource isolation implementation.
 *
 * Manages the portal container element and scoped style injection for app code.
 * Resolves CSS portal variable bleed (Conflict Scenario 4) and provides
 * a managed style injection API to replace direct document.head.appendChild().
 *
 * Phase 152 — implements DomSandbox interface from dom-sandbox.types.ts
 */

import type {
  DomSandbox,
  DomSandboxOptions,
  CreateDomSandbox,
} from './dom-sandbox.types.js'

function toPortalId(appId: string): string {
  const safeId = appId.replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 96) || 'generated-app'
  return `cogo-app-portal-root-${safeId}`
}

function toRenderRootId(appId: string): string {
  const safeId = appId.replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 96) || 'generated-app'
  return `cogo-app-render-root-${safeId}`
}

function scopeCss(css: string, styleScope: string): string {
  return css.replace(/([^{}]+)\{([^{}]*)\}/g, (rule, rawSelectors: string, body: string) => {
    const selectors = rawSelectors.trim()
    if (!selectors || selectors.startsWith('@')) return rule
    if (selectors === 'from' || selectors === 'to' || selectors.includes('%')) return rule
    const scopedSelectors = selectors
      .split(',')
      .map(selector => selector.trim())
      .filter(Boolean)
      .map(selector => {
        if (selector.startsWith(styleScope)) return selector
        if (selector === ':root') return styleScope
        return `${styleScope} ${selector}`
      })
      .join(', ')
    return `${scopedSelectors} {${body}}`
  })
}

function applyStyleToken(el: HTMLElement, key: string, value: string): void {
  if (key.startsWith('--') || key.includes('-')) {
    el.style.setProperty(key, value)
    return
  }
  if (key === 'colorScheme') {
    el.style.colorScheme = value
    return
  }
  if (key === 'fontFamily') {
    el.style.fontFamily = value
    return
  }
  if (key === 'fontWeight') {
    el.style.fontWeight = value
    return
  }
  if (key === 'backgroundColor') {
    el.style.backgroundColor = value
    return
  }
  if (key === 'color') {
    el.style.color = value
    return
  }
  el.style.setProperty(key, value)
}

function applyDesignTokens(el: HTMLElement, tokens?: Record<string, string>): void {
  if (!tokens) return
  Object.entries(tokens).forEach(([k, v]) => {
    applyStyleToken(el, k, v)
  })
}

export const createDomSandbox: CreateDomSandbox = (
  options: DomSandboxOptions
): DomSandbox => {
  // Internal state
  let renderRootEl: HTMLElement | null = null
  let portalEl: HTMLElement | null = null
  const styleRegistry: Map<string, HTMLStyleElement> = new Map()
  let destroyed = false
  let idCounter = 0

  const styleScope: string =
    options.styleScope ?? `[data-app-id="${options.appId}"]`

  function attachRegisteredStyles(): void {
    if (!portalEl || destroyed) return
    for (const el of styleRegistry.values()) {
      if (el.parentElement !== portalEl) {
        portalEl.appendChild(el)
      }
    }
  }

  const domSandbox: DomSandbox = {
    get renderRoot(): HTMLElement | null {
      return renderRootEl
    },

    get portalContainer(): HTMLElement | null {
      return portalEl
    },

    mount(designTokens?: Record<string, string>, hostElement?: HTMLElement): void {
      if (destroyed) return
      const mountTarget = hostElement ?? document.body
      // Keep the app-owned roots stable when React replaces the Studio host
      // element (for example, switching the Page canvas from device-frame to
      // editor mode). The old host can already be detached by the time the
      // canvas layout effect runs; reusing and re-parenting the same roots
      // preserves every active React portal instead of stranding it in the
      // detached host or minting a second root behind the portal reference.
      if (renderRootEl && portalEl) {
        if (renderRootEl.parentElement !== mountTarget) {
          mountTarget.appendChild(renderRootEl)
        }
        if (portalEl.parentElement !== mountTarget) {
          mountTarget.appendChild(portalEl)
        }
        applyDesignTokens(renderRootEl, designTokens)
        applyDesignTokens(portalEl, designTokens)
        attachRegisteredStyles()
        return
      }

      const root = document.createElement('div')
      root.id = toRenderRootId(options.appId)
      root.setAttribute('data-cogo-app-render-root', 'true')
      root.setAttribute('data-sandbox', 'true')
      root.setAttribute('data-app-id', options.appId)
      root.setAttribute('data-cogo-complete-color-tokens', 'true')
      root.setAttribute('data-cogo-design-scope', 'application')
      applyDesignTokens(root, designTokens)

      const el = document.createElement('div')
      el.id = toPortalId(options.appId)
      el.setAttribute('data-sandbox', 'true')
      el.setAttribute('data-app-id', options.appId)
      el.setAttribute('data-cogo-app-portal-root', 'true')
      el.setAttribute('data-cogo-complete-color-tokens', 'true')
      el.setAttribute('data-cogo-design-scope', 'application')
      applyDesignTokens(el, designTokens)

      mountTarget.appendChild(root)
      mountTarget.appendChild(el)
      renderRootEl = root
      portalEl = el
      attachRegisteredStyles()
    },

    unmount(): void {
      if (!portalEl && !renderRootEl) return
      renderRootEl?.remove()
      portalEl?.remove()
      renderRootEl = null
      portalEl = null
      // destroyed remains false — instance is alive; mount() can be called again.
      // Style registry is kept intact.
    },

    updateTokens(tokens: Record<string, string>): void {
      if (destroyed) return
      if (renderRootEl) applyDesignTokens(renderRootEl, tokens)
      if (portalEl) applyDesignTokens(portalEl, tokens)
    },

    injectStyle(css: string, id?: string): HTMLStyleElement {
      if (destroyed) {
        // No-op after destroy — return dummy element not attached to DOM
        return document.createElement('style')
      }

      const styleId = id ?? `cogo-sandbox-style-${idCounter++}`

      // Replace existing element with same id
      if (styleRegistry.has(styleId)) {
        const oldEl = styleRegistry.get(styleId)!
        oldEl.remove()
        styleRegistry.delete(styleId)
      }

      const el = document.createElement('style')
      el.setAttribute('data-sandbox-id', styleId)
      el.setAttribute('data-app-id', options.appId)
      el.setAttribute('data-cogo-app-style-sandbox', 'true')
      el.textContent = scopeCss(css, styleScope)

      styleRegistry.set(styleId, el)
      attachRegisteredStyles()

      return el
    },

    removeStyle(id: string): void {
      const el = styleRegistry.get(id)
      if (!el) return
      el.remove()
      styleRegistry.delete(id)
    },

    destroy(): void {
      // 1. Remove all injected styles
      styleRegistry.forEach(el => el.remove())
      styleRegistry.clear()

      // 2. Remove portal container
      renderRootEl?.remove()
      renderRootEl = null
      portalEl?.remove()
      portalEl = null

      // 3. Mark as permanently destroyed
      destroyed = true
    },
  }

  return domSandbox
}
